import React, { useEffect, useState } from 'react';
import { TopBar } from '../components/TopBar';
import { MainTabs } from '../components/MainTabs';
import { FilterPills } from '../components/FilterPills';
import { ClientCard } from '../components/ClientCard';
import { BottomNav } from '../components/BottomNav';
import { Plus, X, ChevronRight, User, Phone, Mail, MessageSquare, Tag, TrendingUp, MapPin, Home, DollarSign, Bed, Calendar, Search, SlidersHorizontal, Check, ChevronDown } from 'lucide-react';
type ClientCategory = 'New' | 'In-contact' | 'Negotiating' | 'Confirmed' | 'Closed';
const clients = [{
  id: 1,
  name: 'Ahmed Hamdy Mohamed',
  phone: '+20 123 456 7890',
  phoneCountry: 'EG',
  budget: '2M to 2.5 M EGP',
  type: 'BUY' as const,
  initials: 'AH',
  category: 'New' as ClientCategory,
  assignedDate: '2024-01-15',
  tags: ['Hot Lead', 'First Time Buyer'],
  labels: ['VIP', 'Urgent']
}, {
  id: 2,
  name: 'Sarah Ahmed',
  phone: '+20 111 222 3333',
  phoneCountry: 'EG',
  budget: '5M to 7 M EGP',
  type: 'BUY' as const,
  initials: 'SA',
  category: 'In-contact' as ClientCategory,
  assignedDate: '2024-01-10',
  tags: ['Investor'],
  labels: ['High Budget']
}, {
  id: 3,
  name: 'Mohamed Ali',
  phone: '+20 100 555 6666',
  phoneCountry: 'EG',
  budget: '15,000 to 20,000 EGP',
  type: 'RENT' as const,
  initials: 'MA',
  category: 'In-contact' as ClientCategory,
  assignedDate: '2024-01-12',
  tags: ['Rental'],
  labels: ['Follow Up']
}, {
  id: 4,
  name: 'Karim Mahmoud',
  phone: '+20 122 777 8888',
  phoneCountry: 'EG',
  budget: '3M to 4 M EGP',
  type: 'BUY' as const,
  initials: 'KM',
  category: 'Negotiating' as ClientCategory,
  assignedDate: '2024-01-08',
  tags: ['Hot Lead', 'Ready to Buy'],
  labels: ['VIP']
}, {
  id: 5,
  name: 'Nadia Hassan',
  phone: '+20 101 999 0000',
  phoneCountry: 'EG',
  budget: '1.5M to 2 M EGP',
  type: 'BUY' as const,
  initials: 'NH',
  category: 'Confirmed' as ClientCategory,
  assignedDate: '2024-01-05',
  tags: ['First Time Buyer'],
  labels: ['Confirmed']
}, {
  id: 6,
  name: 'Omar Khaled',
  phone: '+20 155 444 3333',
  phoneCountry: 'EG',
  budget: '8M to 10 M EGP',
  type: 'BUY' as const,
  initials: 'OK',
  category: 'Closed' as ClientCategory,
  assignedDate: '2024-01-01',
  tags: ['Investor', 'Repeat Client'],
  labels: ['High Budget', 'VIP']
}];
interface ClientsPageProps {
  onClientClick?: (id: number) => void;
  onNetworkClick?: () => void;
  onInventoryClick?: () => void;
  onRMIClick?: () => void;
  onPropertiesClick?: () => void;
  onCalendarClick?: () => void;
  onChatClick?: () => void;
  onDashboardClick?: () => void;
  onNotificationsClick?: () => void;
}
export function ClientsPage({
  onClientClick,
  onNetworkClick,
  onInventoryClick,
  onRMIClick,
  onPropertiesClick,
  onCalendarClick,
  onChatClick,
  onDashboardClick,
  onNotificationsClick
}: ClientsPageProps) {
  const [activeFilter, setActiveFilter] = useState<string>('New');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [addClientStep, setAddClientStep] = useState<1 | 2>(1);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  // Advanced search filters
  const [assignedDateFrom, setAssignedDateFrom] = useState('');
  const [assignedDateTo, setAssignedDateTo] = useState('');
  const [phoneCountryFilter, setPhoneCountryFilter] = useState<'is' | 'is not'>('is');
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedSalesStages, setSelectedSalesStages] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  // Available options
  const availableCountries = ['EG', 'SA', 'AE', 'KW', 'QA', 'BH', 'OM'];
  const availableSalesStages = ['New', 'In-contact', 'Negotiating', 'Confirmed', 'Closed'];
  const availableTags = ['Hot Lead', 'First Time Buyer', 'Investor', 'Rental', 'Ready to Buy', 'Repeat Client'];
  const availableLabels = ['VIP', 'Urgent', 'High Budget', 'Follow Up', 'Confirmed'];
  // Form state - Step 1
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [leadSource, setLeadSource] = useState('');
  const [marketingCampaign, setMarketingCampaign] = useState('');
  const [notes, setNotes] = useState('');
  // Form state - Step 2
  const [propertyType, setPropertyType] = useState<'BUY' | 'RENT'>('BUY');
  const [location, setLocation] = useState('');
  const [minBudget, setMinBudget] = useState('');
  const [maxBudget, setMaxBudget] = useState('');
  const [bedrooms, setBedrooms] = useState('');
  const [propertyCategory, setPropertyCategory] = useState('');
  const [moveInDate, setMoveInDate] = useState('');
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const handleTabChange = (tab: 'home' | 'clients' | 'properties') => {
    if (tab === 'home' && onDashboardClick) {
      onDashboardClick();
    } else if (tab === 'properties' && onPropertiesClick) {
      onPropertiesClick();
    }
    // 'clients' tab is already active, no action needed
  };
  const handleFilterChange = (filter: string) => {
    setActiveFilter(filter);
  };
  const handleAddClient = () => {
    setShowAddClientModal(true);
    setAddClientStep(1);
  };
  const handleCloseModal = () => {
    setShowAddClientModal(false);
    setAddClientStep(1);
    // Reset form
    setClientName('');
    setClientPhone('');
    setClientEmail('');
    setLeadSource('');
    setMarketingCampaign('');
    setNotes('');
    setPropertyType('BUY');
    setLocation('');
    setMinBudget('');
    setMaxBudget('');
    setBedrooms('');
    setPropertyCategory('');
    setMoveInDate('');
  };
  const handleNextStep = () => {
    if (addClientStep === 1) {
      setAddClientStep(2);
    }
  };
  const handlePreviousStep = () => {
    if (addClientStep === 2) {
      setAddClientStep(1);
    }
  };
  const handleSubmitClient = () => {
    // In a real app, this would save the client data
    alert('Client added successfully!');
    handleCloseModal();
  };
  const handleClearAdvancedFilters = () => {
    setAssignedDateFrom('');
    setAssignedDateTo('');
    setPhoneCountryFilter('is');
    setSelectedCountries([]);
    setSelectedSalesStages([]);
    setSelectedTags([]);
    setSelectedLabels([]);
  };
  const handleApplyAdvancedFilters = () => {
    setShowAdvancedSearch(false);
  };
  const toggleCountry = (country: string) => {
    setSelectedCountries(prev => prev.includes(country) ? prev.filter(c => c !== country) : [...prev, country]);
  };
  const toggleSalesStage = (stage: string) => {
    setSelectedSalesStages(prev => prev.includes(stage) ? prev.filter(s => s !== stage) : [...prev, stage]);
  };
  const toggleTag = (tag: string) => {
    setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };
  const toggleLabel = (label: string) => {
    setSelectedLabels(prev => prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label]);
  };
  const hasActiveAdvancedFilters = assignedDateFrom || assignedDateTo || selectedCountries.length > 0 || selectedSalesStages.length > 0 || selectedTags.length > 0 || selectedLabels.length > 0;
  const filteredClients = clients.filter(client => {
    // Basic search
    const matchesCategory = client.category === activeFilter;
    const matchesSearch = searchQuery === '' || client.name.toLowerCase().includes(searchQuery.toLowerCase()) || client.phone.toLowerCase().includes(searchQuery.toLowerCase());
    // Advanced filters
    const matchesDateFrom = !assignedDateFrom || new Date(client.assignedDate) >= new Date(assignedDateFrom);
    const matchesDateTo = !assignedDateTo || new Date(client.assignedDate) <= new Date(assignedDateTo);
    const matchesCountry = selectedCountries.length === 0 || (phoneCountryFilter === 'is' ? selectedCountries.includes(client.phoneCountry) : !selectedCountries.includes(client.phoneCountry));
    const matchesSalesStage = selectedSalesStages.length === 0 || selectedSalesStages.includes(client.category);
    const matchesTags = selectedTags.length === 0 || selectedTags.some(tag => client.tags.includes(tag));
    const matchesLabels = selectedLabels.length === 0 || selectedLabels.some(label => client.labels.includes(label));
    return matchesCategory && matchesSearch && matchesDateFrom && matchesDateTo && matchesCountry && matchesSalesStage && matchesTags && matchesLabels;
  });
  return <div className="min-h-screen bg-neutral-bg pb-20 font-sans w-full" data-id="element-643">
      <div className="w-full mx-auto bg-white min-h-screen shadow-2xl relative" data-id="element-644">
        <TopBar onCalendarClick={onCalendarClick} onChatClick={onChatClick} data-id="element-645" />
        <MainTabs activeTab="clients" onTabChange={handleTabChange} data-id="element-646" />

        {/* Search Bar */}
        <div className="px-6 pt-4 pb-2" data-id="element-647">
          <div className="flex gap-2" data-id="element-648">
            <div className="relative flex-1" data-id="element-649">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-650" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search by name or phone number" className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary shadow-sm" data-id="element-651" />
            </div>
            <button onClick={() => setShowAdvancedSearch(true)} className={`px-4 py-3 rounded-xl border-2 transition-colors flex items-center gap-2 ${hasActiveAdvancedFilters ? 'bg-secondary border-secondary text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-secondary'}`} data-id="element-652">
              <SlidersHorizontal className="w-5 h-5" data-id="element-653" />
              {hasActiveAdvancedFilters && <span className="text-xs font-bold bg-white text-secondary rounded-full w-5 h-5 flex items-center justify-center" data-id="element-654">
                  {[selectedCountries, selectedSalesStages, selectedTags, selectedLabels].filter(arr => arr.length > 0).length}
                </span>}
            </button>
          </div>
        </div>

        {/* Add Client Button */}
        <div className="px-6 pb-2" data-id="element-655">
          <button onClick={handleAddClient} className="w-full bg-gradient-to-r from-secondary to-secondary-dark hover:from-secondary-dark hover:to-secondary text-white font-bold py-3 rounded-xl transition-colors shadow-lg flex items-center justify-center gap-2" data-id="element-656">
            <Plus className="w-5 h-5" data-id="element-657" />
            Add New Client
          </button>
        </div>

        <FilterPills activeFilter={activeFilter} onFilterChange={handleFilterChange} data-id="element-658" />

        <div className="divide-y divide-gray-50" data-id="element-659">
          {filteredClients.length > 0 ? filteredClients.map(client => <ClientCard key={client.id} id={client.id} name={client.name} budget={client.budget} type={client.type} initials={client.initials} onClick={onClientClick} data-id="element-660" />) : <div className="flex flex-col items-center justify-center py-16 px-4" data-id="element-661">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4" data-id="element-662">
                <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-663">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" data-id="element-664" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-neutral-text mb-1" data-id="element-665">
                No Clients Found
              </h3>
              <p className="text-sm text-gray-500 text-center" data-id="element-666">
                {searchQuery ? `No clients match "${searchQuery}"` : `There are no clients in the "${activeFilter}" category yet.`}
              </p>
            </div>}
        </div>

        {/* Add Client Modal */}
        {showAddClientModal && <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-id="element-667">
            <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl flex flex-col max-h-[90vh] sm:max-h-[85vh]" data-id="element-668">
              {/* Header - Fixed */}
              <div className="flex-shrink-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between rounded-t-3xl sm:rounded-t-2xl" data-id="element-669">
                <div className="flex items-center gap-3" data-id="element-670">
                  <h2 className="text-xl font-bold text-neutral-text" data-id="element-671">
                    Add New Client
                  </h2>
                  <span className="text-sm text-gray-500" data-id="element-672">
                    Step {addClientStep} of 2
                  </span>
                </div>
                <button onClick={handleCloseModal} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-neutral-bg rounded-full transition-colors" data-id="element-673">
                  <X className="w-5 h-5" data-id="element-674" />
                </button>
              </div>

              {/* Progress Indicator - Fixed */}
              <div className="flex-shrink-0 px-6 pt-4 pb-2 bg-white" data-id="element-675">
                <div className="flex items-center gap-2" data-id="element-676">
                  <div className={`flex-1 h-1.5 rounded-full ${addClientStep >= 1 ? 'bg-secondary' : 'bg-gray-200'}`} data-id="element-677" />
                  <div className={`flex-1 h-1.5 rounded-full ${addClientStep >= 2 ? 'bg-secondary' : 'bg-gray-200'}`} data-id="element-678" />
                </div>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto" data-id="element-679">
                {/* Step 1: Contact Information */}
                {addClientStep === 1 && <div className="p-6 space-y-5" data-id="element-680">
                    <div data-id="element-681">
                      <h3 className="font-bold text-neutral-text mb-4 flex items-center gap-2" data-id="element-682">
                        <User className="w-5 h-5 text-secondary" data-id="element-683" />
                        Contact Information
                      </h3>

                      <div className="space-y-4" data-id="element-684">
                        <div data-id="element-685">
                          <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-686">
                            Full Name *
                          </label>
                          <input type="text" value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Enter client name" className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-687" />
                        </div>

                        <div data-id="element-688">
                          <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-689">
                            Phone Number *
                          </label>
                          <div className="relative" data-id="element-690">
                            <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-691" />
                            <input type="tel" value={clientPhone} onChange={e => setClientPhone(e.target.value)} placeholder="+20 123 456 7890" className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-692" />
                          </div>
                        </div>

                        <div data-id="element-693">
                          <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-694">
                            Email Address
                          </label>
                          <div className="relative" data-id="element-695">
                            <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-696" />
                            <input type="email" value={clientEmail} onChange={e => setClientEmail(e.target.value)} placeholder="client@example.com" className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-697" />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div data-id="element-698">
                      <h3 className="font-bold text-neutral-text mb-4 flex items-center gap-2" data-id="element-699">
                        <TrendingUp className="w-5 h-5 text-secondary" data-id="element-700" />
                        Lead Information
                      </h3>

                      <div className="space-y-4" data-id="element-701">
                        <div className="grid grid-cols-2 gap-3" data-id="element-702">
                          <div data-id="element-703">
                            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-704">
                              Lead Source
                            </label>
                            <select value={leadSource} onChange={e => setLeadSource(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary bg-white" data-id="element-705">
                              <option value="" data-id="element-706">Select source</option>
                              <option value="facebook" data-id="element-707">Facebook</option>
                              <option value="instagram" data-id="element-708">Instagram</option>
                              <option value="google" data-id="element-709">Google Ads</option>
                              <option value="referral" data-id="element-710">Referral</option>
                              <option value="website" data-id="element-711">Website</option>
                              <option value="walk-in" data-id="element-712">Walk-in</option>
                              <option value="other" data-id="element-713">Other</option>
                            </select>
                          </div>

                          <div data-id="element-714">
                            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-715">
                              Campaign
                            </label>
                            <input type="text" value={marketingCampaign} onChange={e => setMarketingCampaign(e.target.value)} placeholder="Campaign ID" className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-716" />
                          </div>
                        </div>

                        <div data-id="element-717">
                          <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-718">
                            Notes
                          </label>
                          <div className="relative" data-id="element-719">
                            <MessageSquare className="absolute left-4 top-4 w-5 h-5 text-gray-400" data-id="element-720" />
                            <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Add any additional notes about the client..." rows={4} className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary resize-none" data-id="element-721" />
                          </div>
                        </div>
                      </div>
                    </div>

                    <button onClick={handleNextStep} disabled={!clientName || !clientPhone} className="w-full bg-secondary hover:bg-secondary-dark text-white font-bold py-4 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-722">
                      Next: Search Criteria
                      <ChevronRight className="w-5 h-5" data-id="element-723" />
                    </button>
                  </div>}

                {/* Step 2: Search Criteria */}
                {addClientStep === 2 && <div className="p-6 space-y-5" data-id="element-724">
                    <div data-id="element-725">
                      <h3 className="font-bold text-neutral-text mb-4 flex items-center gap-2" data-id="element-726">
                        <Home className="w-5 h-5 text-secondary" data-id="element-727" />
                        Property Search Criteria
                      </h3>

                      <div className="space-y-4" data-id="element-728">
                        <div data-id="element-729">
                          <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-730">
                            Property Type *
                          </label>
                          <div className="flex gap-3" data-id="element-731">
                            <button onClick={() => setPropertyType('BUY')} className={`flex-1 py-3 rounded-xl font-semibold transition-colors ${propertyType === 'BUY' ? 'bg-secondary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-732">
                              Buy
                            </button>
                            <button onClick={() => setPropertyType('RENT')} className={`flex-1 py-3 rounded-xl font-semibold transition-colors ${propertyType === 'RENT' ? 'bg-secondary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-733">
                              Rent
                            </button>
                          </div>
                        </div>

                        <div data-id="element-734">
                          <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-735">
                            Preferred Location
                          </label>
                          <div className="relative" data-id="element-736">
                            <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-737" />
                            <input type="text" value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g., New Cairo, 5th Settlement" className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-738" />
                          </div>
                        </div>

                        <div data-id="element-739">
                          <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-740">
                            Budget Range *
                          </label>
                          <div className="grid grid-cols-2 gap-3" data-id="element-741">
                            <div className="relative" data-id="element-742">
                              <DollarSign className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-743" />
                              <input type="text" value={minBudget} onChange={e => setMinBudget(e.target.value)} placeholder="Min" className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-744" />
                            </div>
                            <div className="relative" data-id="element-745">
                              <DollarSign className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-746" />
                              <input type="text" value={maxBudget} onChange={e => setMaxBudget(e.target.value)} placeholder="Max" className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-747" />
                            </div>
                          </div>
                        </div>

                        <div data-id="element-748">
                          <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-749">
                            Number of Bedrooms
                          </label>
                          <div className="relative" data-id="element-750">
                            <Bed className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-751" />
                            <select value={bedrooms} onChange={e => setBedrooms(e.target.value)} className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary bg-white" data-id="element-752">
                              <option value="" data-id="element-753">Select bedrooms</option>
                              <option value="1" data-id="element-754">1 Bedroom</option>
                              <option value="2" data-id="element-755">2 Bedrooms</option>
                              <option value="3" data-id="element-756">3 Bedrooms</option>
                              <option value="4" data-id="element-757">4 Bedrooms</option>
                              <option value="5+" data-id="element-758">5+ Bedrooms</option>
                            </select>
                          </div>
                        </div>

                        <div data-id="element-759">
                          <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-760">
                            Property Category
                          </label>
                          <select value={propertyCategory} onChange={e => setPropertyCategory(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary bg-white" data-id="element-761">
                            <option value="" data-id="element-762">Select category</option>
                            <option value="apartment" data-id="element-763">Apartment</option>
                            <option value="villa" data-id="element-764">Villa</option>
                            <option value="townhouse" data-id="element-765">Townhouse</option>
                            <option value="penthouse" data-id="element-766">Penthouse</option>
                            <option value="duplex" data-id="element-767">Duplex</option>
                            <option value="studio" data-id="element-768">Studio</option>
                          </select>
                        </div>

                        <div data-id="element-769">
                          <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-770">
                            Desired Move-in Date
                          </label>
                          <div className="relative" data-id="element-771">
                            <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-772" />
                            <input type="date" value={moveInDate} onChange={e => setMoveInDate(e.target.value)} className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-773" />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-3" data-id="element-774">
                      <button onClick={handlePreviousStep} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-4 rounded-xl transition-colors" data-id="element-775">
                        Back
                      </button>
                      <button onClick={handleSubmitClient} disabled={!minBudget || !maxBudget} className="flex-1 bg-secondary hover:bg-secondary-dark text-white font-bold py-4 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-776">
                        Add Client
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>}

        {/* Advanced Search Modal */}
        {showAdvancedSearch && <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-id="element-777">
            <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl flex flex-col max-h-[90vh]" data-id="element-778">
              {/* Header */}
              <div className="flex-shrink-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between rounded-t-3xl sm:rounded-t-2xl" data-id="element-779">
                <h2 className="text-xl font-bold text-neutral-text" data-id="element-780">
                  Advanced Search
                </h2>
                <button onClick={() => setShowAdvancedSearch(false)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-neutral-bg rounded-full transition-colors" data-id="element-781">
                  <X className="w-5 h-5" data-id="element-782" />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-783">
                {/* Assigned Date Range */}
                <div data-id="element-784">
                  <label className="block text-sm font-bold text-neutral-text mb-3 flex items-center gap-2" data-id="element-785">
                    <Calendar className="w-4 h-4 text-secondary" data-id="element-786" />
                    Assigned Date Range
                  </label>
                  <div className="grid grid-cols-2 gap-3" data-id="element-787">
                    <div data-id="element-788">
                      <label className="block text-xs text-gray-600 mb-1" data-id="element-789">
                        From
                      </label>
                      <input type="date" value={assignedDateFrom} onChange={e => setAssignedDateFrom(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-790" />
                    </div>
                    <div data-id="element-791">
                      <label className="block text-xs text-gray-600 mb-1" data-id="element-792">
                        To
                      </label>
                      <input type="date" value={assignedDateTo} onChange={e => setAssignedDateTo(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-793" />
                    </div>
                  </div>
                </div>

                {/* Phone Country */}
                <div data-id="element-794">
                  <label className="block text-sm font-bold text-neutral-text mb-3 flex items-center gap-2" data-id="element-795">
                    <Phone className="w-4 h-4 text-secondary" data-id="element-796" />
                    Phone Country
                  </label>
                  <div className="flex gap-2 mb-3" data-id="element-797">
                    <button onClick={() => setPhoneCountryFilter('is')} className={`flex-1 py-2 px-4 rounded-lg text-sm font-semibold transition-colors ${phoneCountryFilter === 'is' ? 'bg-secondary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-798">
                      Is
                    </button>
                    <button onClick={() => setPhoneCountryFilter('is not')} className={`flex-1 py-2 px-4 rounded-lg text-sm font-semibold transition-colors ${phoneCountryFilter === 'is not' ? 'bg-secondary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-799">
                      Is Not
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-800">
                    {availableCountries.map(country => <button key={country} onClick={() => toggleCountry(country)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border-2 ${selectedCountries.includes(country) ? 'bg-secondary text-white border-secondary' : 'bg-white text-gray-600 border-gray-200 hover:border-secondary'}`} data-id="element-801">
                        {selectedCountries.includes(country) && <Check className="w-3 h-3 inline mr-1" data-id="element-802" />}
                        {country}
                      </button>)}
                  </div>
                </div>

                {/* Sales Stage */}
                <div data-id="element-803">
                  <label className="block text-sm font-bold text-neutral-text mb-3 flex items-center gap-2" data-id="element-804">
                    <TrendingUp className="w-4 h-4 text-secondary" data-id="element-805" />
                    Sales Stage (Multi-select)
                  </label>
                  <div className="flex flex-wrap gap-2" data-id="element-806">
                    {availableSalesStages.map(stage => <button key={stage} onClick={() => toggleSalesStage(stage)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border-2 ${selectedSalesStages.includes(stage) ? 'bg-secondary text-white border-secondary' : 'bg-white text-gray-600 border-gray-200 hover:border-secondary'}`} data-id="element-807">
                        {selectedSalesStages.includes(stage) && <Check className="w-3 h-3 inline mr-1" data-id="element-808" />}
                        {stage}
                      </button>)}
                  </div>
                </div>

                {/* Tags */}
                <div data-id="element-809">
                  <label className="block text-sm font-bold text-neutral-text mb-3 flex items-center gap-2" data-id="element-810">
                    <Tag className="w-4 h-4 text-secondary" data-id="element-811" />
                    Tags
                  </label>
                  <div className="flex flex-wrap gap-2" data-id="element-812">
                    {availableTags.map(tag => <button key={tag} onClick={() => toggleTag(tag)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border-2 ${selectedTags.includes(tag) ? 'bg-secondary text-white border-secondary' : 'bg-white text-gray-600 border-gray-200 hover:border-secondary'}`} data-id="element-813">
                        {selectedTags.includes(tag) && <Check className="w-3 h-3 inline mr-1" data-id="element-814" />}
                        {tag}
                      </button>)}
                  </div>
                </div>

                {/* Labels */}
                <div data-id="element-815">
                  <label className="block text-sm font-bold text-neutral-text mb-3 flex items-center gap-2" data-id="element-816">
                    <MessageSquare className="w-4 h-4 text-secondary" data-id="element-817" />
                    Labels
                  </label>
                  <div className="flex flex-wrap gap-2" data-id="element-818">
                    {availableLabels.map(label => <button key={label} onClick={() => toggleLabel(label)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border-2 ${selectedLabels.includes(label) ? 'bg-secondary text-white border-secondary' : 'bg-white text-gray-600 border-gray-200 hover:border-secondary'}`} data-id="element-819">
                        {selectedLabels.includes(label) && <Check className="w-3 h-3 inline mr-1" data-id="element-820" />}
                        {label}
                      </button>)}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 p-6 border-t border-gray-100" data-id="element-821">
                <div className="flex gap-3" data-id="element-822">
                  <button onClick={handleClearAdvancedFilters} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-3 rounded-xl transition-colors" data-id="element-823">
                    Clear All
                  </button>
                  <button onClick={handleApplyAdvancedFilters} className="flex-1 bg-secondary hover:bg-secondary-dark text-white font-bold py-3 rounded-xl transition-colors" data-id="element-824">
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>
          </div>}

        <BottomNav onNetworkClick={onNetworkClick} onInventoryClick={onInventoryClick} onRMIClick={onRMIClick} onNotificationsClick={onNotificationsClick} data-id="element-825" />
      </div>
    </div>;
}